import React, { useEffect, useState } from 'react';
import Toast from '../../components/Common/Toast';
import { getModuleSettings, updateModuleSettings } from '../../services/moduleSettingsService';

const IntegrationSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const apiBase = `${window.location.origin}/api/integrations/compras`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getModuleSettings()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setEnabled(Boolean(res.data.integrations_compras_api_enabled));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToast({
            isOpen: true,
            message: 'No se pudo cargar la configuración de integración',
            type: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const onToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      const res = await updateModuleSettings({ integrations_compras_api_enabled: next });
      if (!res.success) throw new Error('No se pudo guardar');
      setToast({
        isOpen: true,
        message: next ? 'Endpoints de integración activados' : 'Endpoints de integración desactivados',
        type: 'success',
      });
    } catch {
      setEnabled(!next);
      setToast({
        isOpen: true,
        message: 'No se pudo guardar la configuración',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Integración</h1>
          <p className="text-gray-600 mt-1">
            Habilita la API para que sistemas externos registren compras de consumo directo.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          {loading ? (
            <p className="text-gray-500">Cargando configuración...</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Activar endpoints</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Cuando está desactivado, las rutas de integración responden 403 y no procesan compras.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  disabled={saving}
                  onClick={onToggle}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 ${
                    enabled ? 'bg-violet-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {enabled && (
                <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900 space-y-2">
                  <p>
                    <span className="font-medium">URL base:</span>{' '}
                    <code className="text-xs bg-white px-2 py-1 rounded border border-violet-100">{apiBase}</code>
                  </p>
                  <p>
                    Cree un usuario técnico con rol limitado y autentíquese con Sanctum (`POST /api/login`).
                  </p>
                  <p>
                    Consulte la documentación en <code>docs/INTEGRACION-COMPRAS-API.md</code>.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default IntegrationSettings;
